import {
  Auth0Client,
  createAuth0Client,
  RedirectLoginOptions,
} from '@auth0/auth0-spa-js'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'

import {
  cleanAuthParamsFromUrl,
  clearAuthState,
  detectAuthProvider,
  detectClientId,
  detectFlow,
  redirectToDiscovery,
} from './auth-bootstrap'
import { STATUS_MESSAGES } from './constants'
import { displayApps, displayStatusMessage } from './utilities'

export let auth0Client: Auth0Client | undefined = undefined

export let cognitoClient: UserManager | undefined = undefined

export async function configureClient() {
  const authProvider = detectAuthProvider()

  if (authProvider === 'cognito') {
    cognitoClient = new UserManager({
      authority: process.env.COGNITO_ISSUER,
      client_id: detectClientId(),
      redirect_uri: `${window.location.origin}?auth_provider=cognito&flow=${detectFlow()}`,
      response_type: 'code',
      scope: 'openid profile email',
      userStore: new WebStorageStateStore({
        store: window.localStorage,
      }),
    })
    return
  }

  auth0Client = await createAuth0Client({
    cacheLocation: 'localstorage',
    authorizationParams: {
      audience: process.env.AUTH0_AUDIENCE_CSP,
      redirect_uri: `${window.location.origin}?auth_provider=auth0&flow=external`,
      scope: 'openid profile email offline_access',
    },
    domain: process.env.AUTH0_DOMAIN,
    clientId: process.env.AUTH0_CLIENT_ID_CSP_PORTAL,
    useRefreshTokens: true,
  })
}

async function getToken() {
  const authProvider = detectAuthProvider()

  if (authProvider === 'cognito') {
    const user = await cognitoClient?.getUser()
    if (!user || user.expired) {
      throw new Error('Expired token!')
    }

    return user.access_token
  }

  try {
    return await auth0Client!.getTokenSilently()
  } catch {
    throw new Error('Expired token!')
  }
}

export async function login() {
  const authProvider = detectAuthProvider()

  if (!authProvider) {
    redirectToDiscovery()
    return
  }

  let isAuthenticated = false
  if (authProvider === 'cognito') {
    const user = await cognitoClient?.getUser()

    isAuthenticated = Boolean(user && !user.expired)
  } else {
    isAuthenticated = await auth0Client!.isAuthenticated()
  }

  console.debug(isAuthenticated, 'isAuthenticated')
  if (isAuthenticated) {
    try {
      const token = await getToken()
      cleanAuthParamsFromUrl()
      displayApps(token)
      return
    } catch (e) {
      console.debug(e, 'Authenticated failed due to stale token.')

      isAuthenticated = false

      console.debug('isAuthentication set to ', isAuthenticated)
    }
  }

  if (!isAuthenticated) {
    console.debug('Initiating handleRedirectCallback')

    const params = new URLSearchParams(window.location.search)

    if (params.get('code')) {
      if (authProvider === 'cognito') {
        await cognitoClient!.signinRedirectCallback()
      } else {
        await auth0Client!.handleRedirectCallback()
      }

      cleanAuthParamsFromUrl()

      try {
        const token = await getToken()
        displayApps(token)
      } catch (e) {
        console.debug('Error while handleRedirectCallback', e)
        displayStatusMessage(STATUS_MESSAGES.serverError.message)
      }
    } else if (params.get('auth_provider')) {
      await loginWithRedirect(params)
    } else {
      redirectToDiscovery()
    }
  }
}

export async function logout() {
  const authProvider = detectAuthProvider()
  let isAuthenticated = false

  if (authProvider === 'cognito') {
    const user = await cognitoClient?.getUser()
    isAuthenticated = Boolean(user && !user.expired)
  } else {
    isAuthenticated = await auth0Client!.isAuthenticated()
  }

  await clearAuthState()

  if (isAuthenticated) {
    if (authProvider === 'cognito') {
      await cognitoClient!.signoutRedirect({
        extraQueryParams: {
          client_id: detectClientId(),
          logout_uri: window.location.origin,
        },
      })
      return
    }
    auth0Client!.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  } else {
    const params = new URLSearchParams(window.location.search)
    await loginWithRedirect(params)
  }
}

export async function loginWithRedirect(params: URLSearchParams) {
  console.debug('loginWithRedirect')

  const authProvider = detectAuthProvider()

  const hint = params.get('hint')

  if (authProvider === 'cognito') {
    await cognitoClient!.signinRedirect({
      ...(hint && {
        extraQueryParams: {
          login_hint: hint,
        },
      }),

      state: {
        returnTo:
          window.location.pathname +
          window.location.search +
          window.location.hash,
      },
    })
    return
  }

  const options: RedirectLoginOptions = {
    authorizationParams: {},
  }

  const connection = params.get('connection')

  if (connection && options.authorizationParams) {
    options.authorizationParams.connection = connection
  }

  if (hint && options.authorizationParams) {
    options.authorizationParams.login_hint = hint
  }

  options.appState = {
    returnTo:
      window.location.pathname + window.location.search + window.location.hash,
  }

  await auth0Client!.loginWithRedirect(options)
}
